import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import { money } from './drawerFormat.js';
import { billingErrorMessage } from './errorMessage.js';
import {
  ACTION_LANES,
  ACTION_MUTATIONS,
  compactBindingLabel,
  EXPORT_LIFECYCLE_OPTIONS,
  mutationLabel,
} from './varianceActionDisplay.js';
import { bindingQuality } from './bindingQuality.js';
import SourceRoundBadge from './SourceRoundBadge.jsx';
import { VARIANCE_ACTION_LABELS } from './varianceActions.js';
import {
  useBillingReconciliationBacAccounts,
  useBillingReconciliationExportPreview,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const ALL = 'all';
const TRACKER_DEFAULT_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;
const TRACKER_VIEW_GROUPED = 'grouped';
const TRACKER_VIEW_FLAT = 'flat';
const ROUND_ALL = 'all';
const GROUPED_TO_FLAT_SORT_KEYS = {
  action: 'action',
  source: 'source',
  product: 'productId',
  productName: 'productName',
  price: 'price',
  dealerName: 'dealerName',
  peopleSoftId: 'peopleSoftId',
  notes: 'notes',
};
const FLAT_TO_GROUPED_SORT_KEYS = {
  action: 'action',
  source: 'source',
  productId: 'product',
  productName: 'productName',
  price: 'price',
  dealerName: 'dealerName',
  peopleSoftId: 'peopleSoftId',
  notes: 'notes',
};

const groupValue = (row) => `BAC ${row.bac || 'No BAC'}`;

const groupRows = (rows) => rows.reduce((acc, row) => {
  const key = groupValue(row);
  if (!acc[key]) acc[key] = [];
  acc[key].push(row);
  return acc;
}, {});

const compareStrings = (left, right) => String(left || '').localeCompare(String(right || ''), undefined, {
  numeric: true,
  sensitivity: 'base',
});

const titleCase = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (letter) => letter.toUpperCase())
  .replace(/\bGm\b/g, 'GM')
  .replace(/\bCpq\b/g, 'CPQ')
  .replace(/\bSf\b/g, 'SF');

const actionTitle = (row) => {
  const raw = row.actionLabel || row.actionType || row.mutation;
  const label = VARIANCE_ACTION_LABELS[raw] || raw || mutationLabel(row.mutation);
  return titleCase(String(label).replace(/\s+file$/i, ''));
};

const priceLabel = (row) => {
  const original = row.originalPrice === null || row.originalPrice === undefined
    ? '--'
    : money(row.originalPrice);
  const proposed = row.proposedPrice === null || row.proposedPrice === undefined
    ? '--'
    : money(row.proposedPrice);
  if ((row.mutation === 'adjust' || row.mutation === 'replace') && original === proposed) return original;
  if (row.mutation === 'adjust' || row.mutation === 'replace') return `${original} -> ${proposed}`;
  if (row.mutation === 'add') return proposed === '--' ? original : proposed;
  if (row.mutation === 'remove') return `${original} -> $0`;
  return original;
};

const priceSortValue = (row) => {
  const proposed = Number(row.proposedPrice);
  if (Number.isFinite(proposed)) return proposed;
  const original = Number(row.originalPrice);
  return Number.isFinite(original) ? original : 0;
};

const productLabel = (row) => {
  const productCode = row.productCode || '--';
  if (!row.replacementProductCode || row.replacementProductCode === row.productCode) return productCode;
  return `${productCode} -> ${row.replacementProductCode}`;
};

const productNameLabel = (row) => {
  const productName = row.productName || '--';
  if (!row.replacementProductName || row.replacementProductName === row.productName) return productName;
  return `${productName} -> ${row.replacementProductName}`;
};

const firstText = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '';

const normalizeNoteLine = (value) => {
  const note = String(value || '').replace(/^Product note:\s*/i, '').trim();
  if (!note) return '';
  return /[.!?]$/.test(note) ? note : `${note}.`;
};

const isProblemRestatingAction = (problem, row) => {
  const normalizedProblem = String(problem || '').toLowerCase();
  if (!normalizedProblem) return false;
  const productCode = String(row.productCode || '').toLowerCase();
  const target = String(row.targetLabel || row.targetSource || row.targetFile || row.sourceLabel || row.source || '').toLowerCase();
  return normalizedProblem.includes('should not be billed')
    && (!productCode || normalizedProblem.includes(productCode))
    && (!target || normalizedProblem.includes(target.replace(' file', '')));
};

const flatNotes = (row) => [
  row.fix,
  row.note,
  row.billingHoldNote,
  row.productNote?.text,
  isProblemRestatingAction(row.problem, row) ? '' : row.problem,
]
  .map(normalizeNoteLine)
  .filter(Boolean)
  .filter((value, index, list) => list.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index);

const dealerNameLabel = (row) => firstText(
  row.dealerName,
  row.salesforceAccountName,
  row.accountName,
);

const peopleSoftIdLabel = (row) => firstText(
  row.peopleSoftId,
  row.peoplesoftId,
  row.peopleSoftID,
  row.PeopleSoftId__c,
  row.PeoplesoftId__c,
  row.PeopleSoft_Id__c,
  row.Peoplesoft_Id__c,
  row.PeopleSoft_ID__c,
  row.Peoplesoft_ID__c,
  row.PeopleSoftID__c,
  row.PeoplesoftID__c,
  row['PeopleSoft ID'],
  row['Peoplesoft ID'],
  row['PeopleSoft Id'],
  row['Peoplesoft Id'],
  row.peoplesoft_id,
);

const accountPeopleSoftIdLabel = (account) => firstText(
  account.peopleSoftId,
  account.peoplesoftId,
  account.peopleSoftID,
  account.PeopleSoftId__c,
  account.PeoplesoftId__c,
  account.PeopleSoft_Id__c,
  account.Peoplesoft_Id__c,
  account.PeopleSoft_ID__c,
  account.Peoplesoft_ID__c,
  account.PeopleSoftID__c,
  account.PeoplesoftID__c,
  account['PeopleSoft ID'],
  account['Peoplesoft ID'],
  account['PeopleSoft Id'],
  account['Peoplesoft Id'],
  account.peoplesoft_id,
  account.people_soft_id,
  account.billingAccountPeopleSoftId,
  account.billingAccountPeoplesoftId,
  account.salesforcePeopleSoftId,
  account.salesforcePeoplesoftId,
);

const accountNameLabel = (account) => firstText(
  account.name,
  account.salesforceAccountName,
  account.accountName,
  account.dealerName,
  account.Name,
  account.Account_Name__c,
  account['Account Name'],
);

const accountSalesforceIdLabel = (account) => firstText(
  account.salesforceId,
  account.salesforceAccountId,
  account.accountId,
  account.id,
  account.Id,
  account.SalesforceId,
  account.Salesforce_ID__c,
  account['Salesforce ID'],
);

const rowAccountCandidates = (row = {}) => {
  const candidates = [
    ...(Array.isArray(row.accountCandidates) ? row.accountCandidates : []),
    ...(Array.isArray(row.peopleSoftIdCandidates) ? row.peopleSoftIdCandidates : []),
    ...(row.salesforceAccountId ? [{
      salesforceAccountId: row.salesforceAccountId,
      salesforceId: row.salesforceAccountId,
      accountId: row.salesforceAccountId,
      peopleSoftId: peopleSoftIdLabel(row),
      name: dealerNameLabel(row),
      accountName: dealerNameLabel(row),
      salesforceAccountName: row.salesforceAccountName || dealerNameLabel(row),
      source: 'action',
    }] : []),
  ];
  const seen = new Set();
  return candidates.filter((candidate) => {
    const salesforceId = accountSalesforceIdLabel(candidate);
    const peopleSoftId = accountPeopleSoftIdLabel(candidate);
    const name = accountNameLabel(candidate);
    if (!salesforceId && !peopleSoftId) return false;
    const key = [salesforceId, peopleSoftId, name].join('|');
    if (!key.replace(/\|/g, '') || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const productIdLabel = (row) => firstText(
  productLabel(row),
);

const supportingProductIdLabel = (row) => firstText(
  row.productId,
  row.productIdentityKey,
  row.inheritedProductIdentityKey,
  row.productIdentity?.key,
  row.manualOrderItemId,
  row.orderItemId,
  row.__orderItemId,
  row.manualReferenceId,
  row.referenceId,
  row.referenceID,
  row.ReferenceId,
);

const roundHoverLabel = (row) => row.roundLabel || (row.roundNumber ? `Round ${row.roundNumber}` : 'No round');

export const productTextColorSx = (row = {}, field = 'productId') => {
  const meta = row.productDisplay || row.display || row.ui || row.actionDisplay || row.metadata || {};
  const productColors = meta.productTextColors || row.productTextColors || {};
  const isName = field === 'productName';
  const value = firstText(
    isName ? row.productNameTextColor : row.productIdTextColor,
    isName ? row.productNameColor : row.productIdColor,
    isName ? productColors.productName : productColors.productId,
    isName ? productColors.name : productColors.id,
    row.productTextColor,
    meta.productTextColor,
  );
  return value ? { color: value } : {};
};

const sourceKind = (row) => {
  const candidates = [row.sourceLabel, row.sourceFile, row.source, row.lane]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  if (candidates.some((value) => value.includes('cpq') || value.includes('peoplesoft'))) return 'cpq';
  if (candidates.some((value) => value.includes('gm'))) return 'gm';
  return 'audit';
};

const statusValue = (row) => String(row.status || row.saveStatus || '').toLowerCase();

const isSavedAction = (row) => ['saved', 'exported', 'resolved'].includes(statusValue(row));

const isExportedAction = (row) => {
  const status = statusValue(row);
  const lifecycle = String(row.exportLifecycleStatus || '').toLowerCase();
  return status === 'exported' || ['exported', 're_exported'].includes(lifecycle);
};

const bindingIsExact = (row) => {
  const label = String(row.accountConfidenceLabel || row.bindingConfidenceLabel || '').toLowerCase();
  const method = String(row.accountMatchMethod || row.bindingMethod || '').toLowerCase();
  const confidence = Number(row.accountConfidence ?? row.bindingConfidence ?? row.matchConfidence);
  return label === 'exact' || method.includes('exact') || confidence >= 95;
};

const bindingHoverLabel = (row) => {
  const quality = bindingQuality(row);
  if (quality.missingEvidenceLink && quality.missingSalesforceAccount) return 'Missing evidence and Salesforce account';
  if (quality.missingEvidenceLink) return 'Missing evidence';
  if (quality.missingSalesforceAccount) return 'Missing Salesforce account';
  return titleCase(row.accountConfidenceLabel || row.bindingConfidenceLabel || (bindingIsExact(row) ? 'exact' : 'not exact'));
};

const sortableValue = (row, key) => {
  if (key === 'bac') return row.bac || '';
  if (key === 'round') return Number(row.roundNumber || 0);
  if (key === 'action') return actionTitle(row);
  if (key === 'saved') return isSavedAction(row) ? 1 : 0;
  if (key === 'source') return sourceKind(row);
  if (key === 'exported') return isExportedAction(row) ? 1 : 0;
  if (key === 'product') return productLabel(row);
  if (key === 'productId') return productIdLabel(row);
  if (key === 'productName') return productNameLabel(row);
  if (key === 'price') return priceSortValue(row);
  if (key === 'binding') return bindingHoverLabel(row);
  if (key === 'dealerName') return dealerNameLabel(row);
  if (key === 'peopleSoftId') return peopleSoftIdLabel(row);
  if (key === 'notes') return flatNotes(row).join(' ');
  return '';
};

const compareTrackerRows = ({ sortBy, sortDirection }) => (left, right) => {
  if (!sortBy) return 0;
  const a = sortableValue(left, sortBy);
  const b = sortableValue(right, sortBy);
  const modifier = sortDirection === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number' && a !== b) return (a - b) * modifier;
  const compared = compareStrings(a, b);
  if (compared) return compared * modifier;
  return compareStrings(left.bac, right.bac) || compareStrings(left.id, right.id);
};

const HeadCell = ({ id, label, align = 'left', sortBy, sortDirection, onSortChange }) => (
  <TableCell align={align}>
    <TableSortLabel
      active={sortBy === id}
      direction={sortBy === id ? sortDirection : 'asc'}
      onClick={() => onSortChange(id)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const bindingTextSx = (row) => {
  const quality = bindingQuality(row);
  if (quality.missingEvidenceLink || quality.missingSalesforceAccount) {
    return { color: 'error.main', fontWeight: 900 };
  }
  return {
    color: bindingIsExact(row) ? 'success.main' : 'warning.main',
    fontWeight: 900,
  };
};

const BooleanStatusIcon = ({ value, trueLabel, falseLabel }) => {
  const Icon = value ? CheckCircleIcon : CancelIcon;
  const label = value ? trueLabel : falseLabel;
  return (
    <Tooltip title={label}>
      <Box
        aria-label={label}
        role="img"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: value ? 'success.main' : 'error.main',
          lineHeight: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

const SourceChip = ({ row }) => {
  const kind = sourceKind(row);
  return <SourceRoundBadge source={kind} />;
};

const actionIconMeta = (row) => {
  const mutation = String(row.mutation || '').toLowerCase();
  if (sourceKind(row) === 'audit' || mutation === 'none' || mutation === 'audit') {
    return { Icon: HelpOutlineIcon, color: 'text.secondary', label: 'Audit' };
  }
  if (mutation === 'add') return { Icon: AddCircleOutlineIcon, color: 'success.main', label: actionTitle(row) || 'Add' };
  if (mutation === 'remove') return { Icon: RemoveCircleOutlineIcon, color: 'error.main', label: actionTitle(row) || 'Remove' };
  if (mutation === 'adjust' || mutation === 'replace') return { Icon: TuneIcon, color: 'warning.main', label: actionTitle(row) || 'Adjust' };
  return { Icon: HelpOutlineIcon, color: 'text.secondary', label: actionTitle(row) || 'Audit' };
};

const ActionIconCell = ({ row }) => {
  const { Icon, color, label } = actionIconMeta(row);
  return (
    <Tooltip title={label}>
      <Box
        aria-label={label}
        role="img"
        sx={{ color, display: 'inline-flex', lineHeight: 0 }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

const ProductCodeCell = ({ row }) => {
  const code = productLabel(row);
  return (
    <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: 'monospace', ...productTextColorSx(row, 'productId') }}>
      {code}
    </Typography>
  );
};

const ProductNameCell = ({ row }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, ...productTextColorSx(row, 'productName') }}>
    {productNameLabel(row)}
  </Typography>
);

const ProductIdCell = ({ row }) => {
  const code = productIdLabel(row);
  const supportingId = supportingProductIdLabel(row);
  return (
    <Tooltip title={supportingId || code} describeChild>
      <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: 'monospace', ...productTextColorSx(row, 'productId') }}>
        {code}
      </Typography>
    </Tooltip>
  );
};

const RoundCell = ({ row }) => {
  const value = row.roundNumber || String(row.roundLabel || '').replace(/^Round\s+/i, '') || '--';
  return (
    <Tooltip title={roundHoverLabel(row)} describeChild>
      <Typography variant="body2" sx={{ fontWeight: 900, fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Tooltip>
  );
};

const AccountValueCell = ({ value }) => (
  <Typography variant="body2" sx={{ fontWeight: value ? 700 : 400, fontFamily: value ? 'inherit' : 'monospace' }}>
    {value || '--'}
  </Typography>
);

const PeopleSoftValueButton = ({ row, onOpen }) => {
  const label = peopleSoftIdLabel(row) || 'View IDs';
  const canOpen = Boolean(row.bac && onOpen);
  return (
    <Button
      size="small"
      variant="text"
      disabled={!canOpen}
      onClick={(event) => {
        event.stopPropagation();
        if (canOpen) onOpen(row);
      }}
      onKeyDown={(event) => event.stopPropagation()}
      sx={{
        justifyContent: 'flex-start',
        minWidth: 0,
        p: 0,
        fontFamily: label === 'View IDs' ? 'inherit' : 'monospace',
        fontWeight: 800,
        textTransform: 'none',
      }}
    >
      {label}
    </Button>
  );
};

const PeopleSoftAccountDialog = ({
  open,
  row,
  accounts = [],
  isLoading = false,
  isError = false,
  error = null,
  copiedId = '',
  onClose,
  onCopy,
}) => {
  const bac = row?.bac || '';
  const [showSlowLoadMessage, setShowSlowLoadMessage] = useState(false);
  useEffect(() => {
    if (!open || !isLoading) {
      setShowSlowLoadMessage(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowSlowLoadMessage(true), 2000);
    return () => clearTimeout(timer);
  }, [isLoading, open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="peoplesoft-account-dialog-title">
      <DialogTitle id="peoplesoft-account-dialog-title">
        PeopleSoft IDs for BAC {bac || '--'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {row && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Tracker value: {peopleSoftIdLabel(row) || 'No unambiguous ID on this action'}
            </Typography>
          )}
          {isLoading && (
            <Alert severity="info">
              {showSlowLoadMessage ? 'Still loading account matches...' : 'Loading AppSheet accounts...'}
            </Alert>
          )}
          {isError && <Alert severity="error">{billingErrorMessage(error, 'AppSheet accounts could not be loaded.')}</Alert>}
          {!isLoading && !isError && accounts.length === 0 && (
            <Alert severity="warning">
              {String(bac).startsWith('99999-')
                ? 'This synthetic BAC has no direct AppSheet BAC account rows. Use the action identity and drawer evidence to assign the missing Salesforce account.'
                : 'No AppSheet Salesforce accounts were found for this BAC.'}
            </Alert>
          )}
          {!isLoading && !isError && accounts.map((account, index) => {
            const peopleSoftId = accountPeopleSoftIdLabel(account);
            const salesforceId = accountSalesforceIdLabel(account);
            const name = accountNameLabel(account) || 'Salesforce account';
            const key = `${salesforceId || name}-${peopleSoftId || index}`;
            return (
              <Paper key={key} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {name}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: salesforceId ? 'monospace' : 'inherit' }}>
                      Salesforce: {salesforceId || '--'}
                    </Typography>
                    {account.status && (
                      <Chip size="small" label={account.status} sx={{ mt: 0.75 }} />
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <Typography variant="body2" sx={{ fontFamily: peopleSoftId ? 'monospace' : 'inherit', fontWeight: 900 }}>
                      {peopleSoftId || '--'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      disabled={!peopleSoftId}
                      onClick={() => onCopy(peopleSoftId)}
                    >
                      {peopleSoftId && copiedId === peopleSoftId ? 'Copied' : 'Copy'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const activationKeyProps = (canOpen, onOpen) => (canOpen ? {
  tabIndex: 0,
  onKeyDown: (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onOpen();
  },
} : {});

const NotesCell = ({ row }) => {
  const notes = flatNotes(row);
  if (!notes.length) return '--';
  return (
    <Stack spacing={0.5}>
      {notes.map((note) => (
        <Typography key={note} variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {note}
        </Typography>
      ))}
    </Stack>
  );
};

const TrackerControls = ({
  lane,
  mutation,
  status,
  searchInput,
  viewMode,
  roundValue,
  roundOptions,
  includeSuperseded,
  onLaneChange,
  onMutationChange,
  onStatusChange,
  onSearchChange,
  onViewModeChange,
  onRoundChange,
  onIncludeSupersededChange,
}) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    spacing={1.5}
    alignItems={{ md: 'center' }}
    useFlexGap
    flexWrap="wrap"
  >
    <TextField
      label="Search actions"
      value={searchInput}
      size="small"
      onChange={(event) => onSearchChange(event.target.value)}
      sx={{ minWidth: { md: 180 }, flex: '1 1 180px' }}
    />
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <InputLabel id="tracker-lane-label">Lane</InputLabel>
      <Select
        labelId="tracker-lane-label"
        value={lane}
        label="Lane"
        onChange={(event) => onLaneChange(event.target.value)}
      >
        <MenuItem value={ALL}>All lanes</MenuItem>
        {ACTION_LANES.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 155 }}>
      <InputLabel id="tracker-mutation-label">Mutation</InputLabel>
      <Select
        labelId="tracker-mutation-label"
        value={mutation}
        label="Mutation"
        onChange={(event) => onMutationChange(event.target.value)}
      >
        <MenuItem value={ALL}>All mutations</MenuItem>
        {ACTION_MUTATIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 145 }}>
      <InputLabel id="tracker-status-label">Status</InputLabel>
      <Select
        labelId="tracker-status-label"
        value={status}
        label="Status"
        onChange={(event) => onStatusChange(event.target.value)}
      >
        <MenuItem value={ALL}>All statuses</MenuItem>
        <MenuItem value="inherited_pending">Inherited pending</MenuItem>
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="saved">Saved</MenuItem>
        <MenuItem value="exported">Exported</MenuItem>
        <MenuItem value="resolved">Resolved</MenuItem>
        {EXPORT_LIFECYCLE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>Lifecycle: {option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 175 }}>
      <InputLabel id="tracker-round-label">Round</InputLabel>
      <Select
        labelId="tracker-round-label"
        value={roundValue}
        label="Round"
        onChange={(event) => onRoundChange(event.target.value)}
      >
        <MenuItem value={ROUND_ALL}>All rounds</MenuItem>
        {roundOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <Button
      size="small"
      variant={includeSuperseded ? 'outlined' : 'contained'}
      onClick={() => onIncludeSupersededChange(!includeSuperseded)}
    >
      {includeSuperseded ? 'Show superseded' : 'Hide superseded'}
    </Button>
    <ToggleButtonGroup
      exclusive
      size="small"
      value={viewMode}
      onChange={(_event, nextView) => {
        if (nextView) onViewModeChange(nextView);
      }}
      aria-label="Action tracker view"
      sx={{
        alignSelf: { xs: 'stretch', md: 'center' },
        flexShrink: 0,
        '& .MuiToggleButton-root': { minWidth: 72 },
      }}
    >
      <ToggleButton value={TRACKER_VIEW_GROUPED} aria-label="Grouped action tracker view">
        Grouped
      </ToggleButton>
      <ToggleButton value={TRACKER_VIEW_FLAT} aria-label="Flat action tracker view">
        Flat
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
);

const GroupedTrackerTable = ({
  groupedRows,
  sortBy,
  sortDirection,
  onSortChange,
  onOpenRow,
  onOpenBac,
  onOpenPeopleSoftAccounts,
}) => (
  <Table size="small" aria-label="variance action tracker">
    <TableHead>
      <TableRow>
        <HeadCell id="action" label="Action" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="saved" label="Saved" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="source" label="Source" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="exported" label="Exported" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="product" label="Product" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="productName" label="P. Name" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="price" label="Price" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="dealerName" label="Dealer Name" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="peopleSoftId" label="PeopleSoft ID" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="binding" label="Binding" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="notes" label="Notes" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
      </TableRow>
    </TableHead>
    <TableBody>
      {Object.entries(groupedRows).map(([group, groupRowsForKey]) => (
        <React.Fragment key={group}>
          <TableRow>
            <TableCell colSpan={11} sx={{ bgcolor: 'action.hover', fontWeight: 900 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 900, fontFamily: 'monospace' }}>
                  {group}
                </Typography>
                <Chip size="small" label={`${groupRowsForKey.length} action${groupRowsForKey.length === 1 ? '' : 's'}`} />
                {dealerNameLabel(groupRowsForKey[0]) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {dealerNameLabel(groupRowsForKey[0])}
                  </Typography>
                )}
              </Stack>
            </TableCell>
          </TableRow>
          {groupRowsForKey.map((row) => {
            const saved = isSavedAction(row);
            const exported = isExportedAction(row);
            const canOpen = Boolean(onOpenBac && row.bac);
            return (
              <TableRow
                key={row.id}
                hover
                onClick={canOpen ? () => onOpenRow(row) : undefined}
                {...activationKeyProps(canOpen, () => onOpenRow(row))}
                sx={{ cursor: canOpen ? 'pointer' : 'default' }}
              >
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ActionIconCell row={row} />
                    {(row.missingEvidenceLink || row.missingSalesforceAccount) && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', fontWeight: 800 }}>
                        {[
                          row.missingEvidenceLink ? 'Missing evidence' : '',
                          row.missingSalesforceAccount ? 'Missing SF account' : '',
                        ].filter(Boolean).join(' + ')}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <BooleanStatusIcon value={saved} trueLabel="Saved" falseLabel="Not saved" />
                </TableCell>
                <TableCell><SourceChip row={row} /></TableCell>
                <TableCell>
                  <BooleanStatusIcon value={exported} trueLabel="Exported" falseLabel="Not exported" />
                </TableCell>
                <TableCell>
                  <ProductCodeCell row={row} />
                </TableCell>
                <TableCell><ProductNameCell row={row} /></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{priceLabel(row)}</TableCell>
                <TableCell><AccountValueCell value={dealerNameLabel(row)} /></TableCell>
                <TableCell><PeopleSoftValueButton row={row} onOpen={onOpenPeopleSoftAccounts} /></TableCell>
                <TableCell>
                  <Tooltip title={bindingHoverLabel(row)} describeChild>
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', ...bindingTextSx(row) }}>
                      {compactBindingLabel(row)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell><NotesCell row={row} /></TableCell>
              </TableRow>
            );
          })}
        </React.Fragment>
      ))}
    </TableBody>
  </Table>
);

const FlatTrackerTable = ({
  rows,
  sortBy,
  sortDirection,
  onSortChange,
  onOpenRow,
  onOpenBac,
  onOpenPeopleSoftAccounts,
}) => (
  <Table size="small" aria-label="flat variance action tracker">
    <TableHead>
      <TableRow>
        <HeadCell id="round" label="R" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="bac" label="BAC" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="dealerName" label="Dealer Name" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="peopleSoftId" label="PeopleSoft ID" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="source" label="Source" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="action" label="Action" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="productId" label="Product ID" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="productName" label="P. Name" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="price" label="Price" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        <HeadCell id="notes" label="Notes" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => {
        const canOpen = Boolean(onOpenBac && row.bac);
        return (
          <TableRow
            key={row.id}
            hover
            onClick={canOpen ? () => onOpenRow(row) : undefined}
            {...activationKeyProps(canOpen, () => onOpenRow(row))}
            sx={{ cursor: canOpen ? 'pointer' : 'default' }}
          >
            <TableCell><RoundCell row={row} /></TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 900 }}>
                {row.bac || '--'}
              </Typography>
            </TableCell>
            <TableCell><AccountValueCell value={dealerNameLabel(row)} /></TableCell>
            <TableCell><PeopleSoftValueButton row={row} onOpen={onOpenPeopleSoftAccounts} /></TableCell>
            <TableCell><SourceChip row={row} /></TableCell>
            <TableCell><ActionIconCell row={row} /></TableCell>
            <TableCell><ProductIdCell row={row} /></TableCell>
            <TableCell><ProductNameCell row={row} /></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{priceLabel(row)}</TableCell>
            <TableCell><NotesCell row={row} /></TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const VarianceActionTrackerPanel = ({ runId, onOpenBac = null, iterations = [] }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lane, setLane] = useState(ALL);
  const [mutation, setMutation] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [roundValue, setRoundValue] = useState(ROUND_ALL);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TRACKER_DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState(TRACKER_VIEW_FLAT);
  const [peopleSoftAccountRow, setPeopleSoftAccountRow] = useState(null);
  const [copiedPeopleSoftId, setCopiedPeopleSoftId] = useState('');
  const normalizedSearch = search.trim();
  const selectedRowAccounts = useMemo(() => rowAccountCandidates(peopleSoftAccountRow || {}), [peopleSoftAccountRow]);
  const roundOptions = useMemo(() => (iterations || [])
    .map((iteration) => ({
      value: String(iteration.roundNumber || iteration.round || ''),
      label: iteration.roundNumber ? `Round ${iteration.roundNumber}` : iteration.label || iteration.id,
    }))
    .filter((option) => option.value), [iterations]);
  const previewParams = useMemo(() => ({
    runId,
    view: 'tracker',
    lane: lane === ALL ? null : lane,
    mutation: mutation === ALL ? null : mutation,
    status: status === ALL ? null : status,
    search: normalizedSearch || null,
    roundMode: roundValue === ROUND_ALL ? 'all' : 'current',
    roundNumber: roundValue === ROUND_ALL ? null : roundValue,
    includeSuperseded: includeSuperseded ? 'true' : null,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  }), [includeSuperseded, lane, mutation, normalizedSearch, page, roundValue, rowsPerPage, runId, status]);
  const previewQuery = useBillingReconciliationExportPreview(previewParams, { enabled: Boolean(runId) });
  const selectedRowBac = peopleSoftAccountRow?.bac || '';
  const selectedRowIsSyntheticBac = String(selectedRowBac).startsWith('99999-');
  const peopleSoftAccountsQuery = useBillingReconciliationBacAccounts(
    { runId, bac: selectedRowBac || null, limit: 100 },
    { enabled: Boolean(runId && selectedRowBac && !selectedRowIsSyntheticBac) }
  );
  const displayedPeopleSoftAccounts = useMemo(() => {
    const fetchedAccounts = Array.isArray(peopleSoftAccountsQuery.data) ? peopleSoftAccountsQuery.data : [];
    if (fetchedAccounts.length) return fetchedAccounts;
    return selectedRowAccounts;
  }, [peopleSoftAccountsQuery.data, selectedRowAccounts]);
  const rows = useMemo(() => previewQuery.data?.rows || [], [previewQuery.data?.rows]);
  const summary = previewQuery.data?.summary || { byLane: {}, byMutation: {}, draftCount: 0, total: 0 };
  const rowsMeta = previewQuery.data?.rowsMeta || {};
  const filteredCount = Number.isFinite(Number(rowsMeta.filteredTotal)) ? Number(rowsMeta.filteredTotal) : rows.length;
  const lastValidPage = Math.max(0, Math.ceil(filteredCount / rowsPerPage) - 1);
  const sortedRows = useMemo(
    () => (sortBy ? [...rows].sort(compareTrackerRows({ sortBy, sortDirection })) : rows),
    [rows, sortBy, sortDirection],
  );
  const groupedRows = useMemo(() => groupRows(sortedRows), [sortedRows]);
  const resetPage = (setter) => (value) => {
    setPage(0);
    setter(value);
  };
  const openActionBac = (row) => {
    if (!onOpenBac || !row.bac) return;
    onOpenBac(row.bac, {
      source: 'action_tracker',
      severity: row.missingSalesforceAccount || row.missingEvidenceLink ? 'warning' : 'info',
      title: 'Action tracker',
      summary: row.fix || row.problem || 'Review saved action',
      actionId: row.actionId || row.id,
      focusActionId: row.actionId || row.id,
      varianceId: row.varianceId || null,
      roundRunId: row.roundRunId || null,
      productCode: row.productCode,
      productName: row.productName,
      dealerName: dealerNameLabel(row),
    });
  };
  const handleSortChange = (nextSortBy) => {
    setPage(0);
    if (sortBy === nextSortBy) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(nextSortBy);
    setSortDirection('asc');
  };
  const handleViewModeChange = (nextViewMode) => {
    setViewMode(nextViewMode);
    setSortBy((currentSortBy) => {
      if (!currentSortBy) return currentSortBy;
      const sortMap = nextViewMode === TRACKER_VIEW_FLAT ? GROUPED_TO_FLAT_SORT_KEYS : FLAT_TO_GROUPED_SORT_KEYS;
      return sortMap[currentSortBy] || null;
    });
  };
  const openPeopleSoftAccounts = (row) => {
    setCopiedPeopleSoftId('');
    setPeopleSoftAccountRow(row);
  };
  const closePeopleSoftAccounts = () => {
    setPeopleSoftAccountRow(null);
    setCopiedPeopleSoftId('');
  };
  const copyPeopleSoftId = async (peopleSoftId) => {
    await navigator.clipboard.writeText(peopleSoftId);
    setCopiedPeopleSoftId(peopleSoftId);
  };

  useEffect(() => {
    setPage(0);
  }, [runId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (page > lastValidPage) setPage(lastValidPage);
  }, [lastValidPage, page]);

  if (!runId) {
    return <Alert severity="info">Create or select a round before reviewing saved visual-builder actions.</Alert>;
  }
  if (previewQuery.isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton height={48} />
        <Skeleton height={240} />
      </Stack>
    );
  }
  if (previewQuery.isError) {
    return <Alert severity="error">{billingErrorMessage(previewQuery.error, 'Variance tracker could not be loaded.')}</Alert>;
  }

  return (
    <Stack spacing={2}>
      {summary.draftCount > 0 && (
        <Alert severity="warning">
          {summary.draftCount} pending action{summary.draftCount === 1 ? '' : 's'} still need{summary.draftCount === 1 ? 's' : ''} save/confirm before export.
        </Alert>
      )}
      <Paper aria-label="Action tracker filters" variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <TrackerControls
          lane={lane}
          mutation={mutation}
          status={status}
          searchInput={searchInput}
          viewMode={viewMode}
          roundValue={roundValue}
          roundOptions={roundOptions}
          includeSuperseded={includeSuperseded}
          onLaneChange={resetPage(setLane)}
          onMutationChange={resetPage(setMutation)}
          onStatusChange={resetPage(setStatus)}
          onSearchChange={setSearchInput}
          onViewModeChange={handleViewModeChange}
          onRoundChange={resetPage(setRoundValue)}
          onIncludeSupersededChange={resetPage(setIncludeSuperseded)}
        />
      </Paper>
      {includeSuperseded && (
        <Alert severity="info">
          Showing superseded actions too. {filteredCount.toLocaleString()} action{filteredCount === 1 ? '' : 's'} match the current filters.
        </Alert>
      )}

      {filteredCount === 0 ? (
        <Alert severity={summary.total ? 'info' : 'success'}>
          {summary.total ? 'No saved actions match the current tracker filters.' : 'No saved visual-builder actions yet.'}
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">This tracker page is empty after the latest refresh; returning to the nearest available page.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
          {viewMode === TRACKER_VIEW_FLAT ? (
            <FlatTrackerTable
              rows={sortedRows}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onOpenRow={openActionBac}
              onOpenBac={onOpenBac}
              onOpenPeopleSoftAccounts={openPeopleSoftAccounts}
            />
          ) : (
            <GroupedTrackerTable
              groupedRows={groupedRows}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onOpenRow={openActionBac}
              onOpenBac={onOpenBac}
              onOpenPeopleSoftAccounts={openPeopleSoftAccounts}
            />
          )}
          <TablePagination
            component="div"
            count={filteredCount}
            page={Math.min(page, lastValidPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </TableContainer>
      )}
      <PeopleSoftAccountDialog
        open={Boolean(peopleSoftAccountRow)}
        row={peopleSoftAccountRow}
        accounts={displayedPeopleSoftAccounts}
        isLoading={!selectedRowIsSyntheticBac && peopleSoftAccountsQuery.isLoading}
        isError={peopleSoftAccountsQuery.isError}
        error={peopleSoftAccountsQuery.error}
        copiedId={copiedPeopleSoftId}
        onClose={closePeopleSoftAccounts}
        onCopy={copyPeopleSoftId}
      />
    </Stack>
  );
};

export default VarianceActionTrackerPanel;
